import React, { useCallback } from "react";
import { colors2, getVal, setVal, icons, transition, UI_BODY_SEMIBOLD_SM } from "oolib";
import styled from "styled-components";
import TooltipV2 from "@/components/UI/Tooltip";
import { DataFieldInputSelector } from "@/components/DataFieldInputSelector";
import { useBarChartConfig } from "../../WidgetBarChart/ConfigForm/ConfigForm_Context";
import { getPickerProp } from "../../utils/pickerVariantConfig";
import { useGetAllOptionsForDimension2 } from "../../WidgetBarChart/utils/useGetAllOptionsForDimension2";
import { DATA_KEYS } from "../../WidgetBarChart/ConfigForm/DATA_KEYS";
import { CollapseTransition } from "../../utils/CollapseTransition";
import { NumberFieldConfig } from "../../WidgetBarChart/ConfigForm/NumberFieldConfig";
import { useReportLandingContext } from "../../../../ReportLandingContext";
import { useVisibilityModifier } from "../../utils/useVisibilityModifier";

const { ArrowsClockwise } = icons;

const StyledSwapButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0 auto;
  padding: 0.4rem 1rem;
  border: 1px solid ${colors2.grey10};
  border-radius: 1.4rem;
  background: ${colors2.white};
  cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
  ${transition('background 0.15s', 'border-color 0.15s')}

  &:hover {
    background: ${({ $disabled }) => $disabled ? colors2.white : colors2.grey05};
    border-color: ${({ $disabled }) => $disabled ? colors2.grey10 : colors2.grey30};
  }

  & > * {
    opacity: ${({ $disabled }) => $disabled ? 0.4 : 1};
  }
`;

const SwapInputsButton = () => {
  const { widgetConfig, handleFlipDimensions } = useBarChartConfig();
  const dim1 = getVal(widgetConfig, DATA_KEYS.dimension1);
  const dim2 = getVal(widgetConfig, DATA_KEYS.dimension2);
  const isDisabled = !dim1 || !dim2;

  const tooltipText = !dim1
    ? "Select X-axis first"
    : !dim2
      ? "Select Y-axis first"
      : "Swap X and Y axis inputs";

  return (
    <TooltipV2 text={tooltipText} placement="top">
      <StyledSwapButton
        $disabled={isDisabled}
        onClick={isDisabled ? undefined : handleFlipDimensions}
      >
        <ArrowsClockwise size={14} color={colors2.grey80} weight="bold" />
        <UI_BODY_SEMIBOLD_SM style={{ color: colors2.grey80 }}>
          Swap Inputs
        </UI_BODY_SEMIBOLD_SM>
      </StyledSwapButton>
    </TooltipV2>
  );
};

export const HeatMapDataSection = () => {
  const {
    widgetConfig,
    setConfig,
    underEditWidgetIdx,
    optionsForDim1,
    dimensionsInputPropsConfig,
    arithmeticState,
    handleDimension2Change,
  } = useBarChartConfig();

  const { dataFields, handleFieldAChange } = arithmeticState;

  const { widgetDataStore } = useReportLandingContext();
  const data = widgetDataStore?.[widgetConfig.id];

  const dim1Value = getVal(widgetConfig, DATA_KEYS.dimension1);
  const dim2Value = getVal(widgetConfig, DATA_KEYS.dimension2);

  const dim2Options = useGetAllOptionsForDimension2({ dimension1Value: dim1Value });

  const updateConfig = useCallback((updaterFn) => {
    setConfig((prev) => {
      let w = JSON.parse(JSON.stringify(prev[underEditWidgetIdx]));
      w = updaterFn(w);
      const full = [...prev];
      full[underEditWidgetIdx] = w;
      return full;
    });
  }, [setConfig, underEditWidgetIdx]);

  const dim1VisibilityModifier = useVisibilityModifier({
    dimension: 1,
    widgetConfig,
    dataKeys: DATA_KEYS,
    data,
    isNumberInput: dim1Value?.comp === 'NumberInput',
    updateConfig,
  });

  const dim2VisibilityModifier = useVisibilityModifier({
    dimension: 2,
    widgetConfig,
    dataKeys: DATA_KEYS,
    data,
    isNumberInput: dim2Value?.comp === 'NumberInput',
    updateConfig,
  });

  return (
    <>
      {/* X-axis field */}
      <DataFieldInputSelector
        indicator={{ label: "X" }}
        options={optionsForDim1}
        value={dataFields.A}
        onChange={(id, v) => handleFieldAChange(v)}
        placeholder={dimensionsInputPropsConfig.input1?.label || "Vertical Axis Input"}
        modifier={dim1VisibilityModifier}
        picker={getPickerProp({ widgetComp: 'WidgetHeatMapGrid', slot: 'dimension1' })}
      />

      <NumberFieldConfig
        dimensionKey="dimension1"
        dimensionValue={dim1Value}
        dataKeys={DATA_KEYS}
        thisWidgetConfig={widgetConfig}
        setConfig={setConfig}
        underEditWidgetIdx={underEditWidgetIdx}
      />

      <SwapInputsButton />

      {/* Y-axis field */}
      <DataFieldInputSelector
        indicator={{ label: "Y" }}
        options={dim2Options}
        value={dim2Value}
        onChange={(id, v) => handleDimension2Change(null, v)}
        placeholder={dimensionsInputPropsConfig.input2?.label || "Horizontal Axis Input"}
        disabled={!dim1Value}
        modifier={dim2VisibilityModifier}
        picker={getPickerProp({ widgetComp: 'WidgetHeatMapGrid', slot: 'dimension2' })}
      />

      <CollapseTransition isVisible={!!dim2Value}>
        <NumberFieldConfig
          dimensionKey="dimension2"
          dimensionValue={dim2Value}
          dataKeys={DATA_KEYS}
          thisWidgetConfig={widgetConfig}
          setConfig={setConfig}
          underEditWidgetIdx={underEditWidgetIdx}
        />
      </CollapseTransition>
    </>
  );
};
